import { RPCCacheStrategy } from "./types.js";
import { useRPCContext } from "../../contexts/RPCContext.js";
import { useRPCDefaultOpts } from "./defaults.js";
import { useCallback, useDebugValue, useEffect, useRef, useState } from "react";
import { onBrowserVisibilityChange } from "@therms/web-js";
// src/hooks/useRPC/index.ts
/**
* This function is a utility for other parts of the software to signal to any hooks that are mounted to refresh.
*
* @note Use this sparingly**
*
* @param reason {string}
*/
function rpcHookRefreshSignal(reason) {
	console.info(`⦿ rpcHookRefreshSignal(${reason})`);
	window.dispatchEvent(new CustomEvent("RPC_HOOK_REFRESH"));
}
/**
* The reads every mounted `useRPC` has on the wire, by hook key.
*
* Module-level rather than per hook: the transport merges identical in-flight
* calls across *all* callers, so a refresh can join a read some other hook sent
* as easily as one of its own. `sentAt` is when that read went out, which is
* what a call that joined it is really the answer to.
*/
var inFlightByKey = /* @__PURE__ */ new Map();
/**
* A cached response, unless it is a failure.
*
* The transport caches `success: false` responses too. Handing one back would
* show an old error as the current answer, or — on a remote-first fallback —
* replace data that was fine with an error's empty payload.
*/
var usableCache = (cached) => cached?.success ? cached : void 0;
function useRPC(request, args, opts = useRPCDefaultOpts) {
	useDebugValue(request);
	const rpcContext = useRPCContext();
	const key = JSON.stringify({
		args,
		request
	});
	opts = {
		...useRPCDefaultOpts,
		...rpcContext.useRPCDefaultOpts || {},
		...opts || {}
	};
	const rpcClient = rpcContext?.rpcClient;
	if (!rpcClient) throw new Error("a RPCClient instance is required in opts or from RPCContext");
	const [answer, setAnswer] = useState(() => {
		const cached = opts.cacheStrategy !== RPCCacheStrategy.remoteFirst ? usableCache(rpcClient.getCallCache(request, args)) : void 0;
		return {
			args: cached ? args : void 0,
			fromServer: false,
			remoteResponses: 0,
			response: cached
		};
	});
	const { remoteResponses, response } = answer;
	const [firstCallCompleted, setFirstCallCompleted] = useState(false);
	const [capturedError, setCapturedError] = useState(void 0);
	const [isCalling, setIsCalling] = useState(!!opts.fire && !response);
	const stateRef = useRef({
		callKey: "",
		callSeq: 0,
		firstCallCompleted,
		inFlightKey: "",
		isCalling,
		isMounted: true,
		opts,
		response,
		supersededSeq: 0
	});
	const getState = () => stateRef.current;
	stateRef.current.firstCallCompleted = firstCallCompleted;
	stateRef.current.isCalling = isCalling;
	stateRef.current.response = response;
	stateRef.current.opts = opts;
	const call = useCallback(async (key, callRequest, callArgs, callOpts = useRPCDefaultOpts, silentCall) => {
		const state = getState();
		state.callKey = key;
		const seq = ++state.callSeq;
		state.isCalling = true;
		if (!silentCall) {
			setIsCalling(true);
			setCapturedError(void 0);
		}
		state.lastCall = () => refresh(key, callRequest, callArgs, callOpts, true);
		const cachedResponse = usableCache(rpcClient.getCallCache(callRequest, callArgs));
		if (cachedResponse && callOpts.cacheStrategy !== RPCCacheStrategy.remoteFirst) {
			setAnswer((prev) => ({
				...prev,
				args: callArgs,
				fromServer: false,
				requestedAt: void 0,
				response: cachedResponse
			}));
			setIsCalling(false);
			if (callOpts.cacheStrategy === RPCCacheStrategy.cacheOnly) return cachedResponse;
		}
		state.inFlightKey = state.callKey;
		const thisCallKey = state.inFlightKey;
		const applies = () => {
			const state = getState();
			return thisCallKey === state.callKey && seq > state.supersededSeq;
		};
		const joined = inFlightByKey.get(key);
		const sentAt = performance.now();
		const promise = rpcClient.call(callRequest, callArgs, {
			timeout: callOpts.timeout,
			transport: callOpts.transport
		});
		if (!joined) {
			const clear = () => {
				if (inFlightByKey.get(key)?.promise === promise) inFlightByKey.delete(key);
			};
			inFlightByKey.set(key, {
				promise,
				sentAt
			});
			promise.then(clear, clear);
		}
		try {
			const callResponse = await promise;
			if (getState().isMounted && applies()) {
				setAnswer((prev) => ({
					args: callArgs,
					fromServer: true,
					remoteResponses: prev.remoteResponses + 1,
					requestedAt: joined ? joined.sentAt : sentAt,
					response: callResponse
				}));
				setIsCalling(false);
				setCapturedError(void 0);
			}
			return callResponse;
		} catch (err) {
			const state = getState();
			console.warn(`useRPC#call(${callRequest}) caught: `, err);
			if (state.isMounted && applies()) {
				if (callOpts.cacheStrategy === RPCCacheStrategy.remoteFirst && cachedResponse) setAnswer((prev) => ({
					...prev,
					args: callArgs,
					fromServer: false,
					requestedAt: void 0,
					response: cachedResponse
				}));
				if (!silentCall) setCapturedError(true);
			}
			return Promise.reject(err);
		} finally {
			const state = getState();
			if (applies()) {
				state.inFlightKey = "";
				if (state.isMounted) {
					setIsCalling(false);
					if (!state.firstCallCompleted) setFirstCallCompleted(true);
				}
			}
		}
	}, []);
	/**
	* `call`, but never answered by a read that was already on its way.
	*
	* With `refreshAfterInFlight`, a refresh asked while the same key is in
	* flight waits for that call to settle and then sends its own, and the
	* in-flight call's answer is not applied — it was asked before whatever the
	* refresh is for. Any further refreshes while it waits share the one
	* follow-up: they were all asked after the in-flight call went out, and one
	* request sent after all of them answers every one.
	*/
	const refresh = useCallback((key, callRequest, callArgs, callOpts = useRPCDefaultOpts, silentCall) => {
		const inFlight = inFlightByKey.get(key);
		if (!callOpts.refreshAfterInFlight || !inFlight) return call(key, callRequest, callArgs, callOpts, silentCall);
		const state = getState();
		if (state.followUp?.key === key) {
			if (!silentCall) {
				state.followUp.silent = false;
				setIsCalling(true);
				setCapturedError(void 0);
			}
			return state.followUp.promise;
		}
		if (state.callKey === key) state.supersededSeq = state.callSeq;
		if (!silentCall) {
			setIsCalling(true);
			setCapturedError(void 0);
		}
		const followUp = {
			key,
			promise: inFlight.promise.then(() => void 0, () => void 0).then(() => {
				const state = getState();
				if (state.followUp === followUp) state.followUp = void 0;
				if (!state.isMounted || state.callKey !== key) return inFlight.promise;
				return call(key, callRequest, callArgs, callOpts, followUp.silent);
			}),
			silent: !!silentCall
		};
		state.followUp = followUp;
		return followUp.promise;
	}, []);
	useEffect(() => {
		const state = getState();
		if (key !== state.callKey) {
			setCapturedError(void 0);
			if (opts.fire) {
				if (!opts.persistPreviousData) setAnswer((prev) => ({
					...prev,
					args: void 0,
					fromServer: false,
					requestedAt: void 0,
					response: void 0
				}));
				call(key, request, args, opts).catch(() => null);
			}
		}
	}, [
		args,
		request,
		opts
	]);
	useEffect(() => {
		let shouldCallOnVisible = false;
		let visibilityTimeout;
		stateRef.current.isMounted = true;
		const rpcHookRefreshSignal = () => {
			stateRef.current.lastCall?.().catch(() => null);
		};
		addEventListener("RPC_HOOK_REFRESH", rpcHookRefreshSignal);
		const unsubscribe = onBrowserVisibilityChange((visible) => {
			if (!visible && opts.callOnVisible) visibilityTimeout = setTimeout(() => {
				shouldCallOnVisible = true;
			}, opts.callOnVisibleTimeout || 0);
			else if (opts.callOnVisible && shouldCallOnVisible && opts.fire) {
				stateRef.current.lastCall?.().catch(() => null);
				shouldCallOnVisible = false;
			}
		});
		return () => {
			removeEventListener("RPC_HOOK_REFRESH", rpcHookRefreshSignal);
			stateRef.current.isMounted = false;
			clearTimeout(visibilityTimeout);
			unsubscribe();
		};
	}, []);
	useEffect(() => {
		if (firstCallCompleted) return rpcClient.subscribe({
			args,
			request
		}, (data) => {
			if (stateRef.current.opts?.fire && !stateRef.current.isCalling) setAnswer((prev) => {
				if (prev.response) return {
					...prev,
					response: {
						...prev.response,
						...data
					}
				};
				return prev;
			});
		});
	}, [firstCallCompleted]);
	return {
		call: () => refresh(key, request, args, opts),
		code: response?.code,
		data: response?.data,
		dataArgs: answer.args,
		dataFromServer: answer.fromServer,
		dataRequestedAt: answer.requestedAt,
		error: capturedError || void 0,
		isCalling,
		message: response?.message,
		remoteResponses,
		success: response?.success || false
	};
}
export { RPCCacheStrategy, rpcHookRefreshSignal, useRPC };

//# sourceMappingURL=index.js.map